import { memo } from 'react';
import { Grid } from '@mui/material';
import { InvestmentFinancialMetricsDTO, MetricValueDTO } from '@inversor-hub/types';
import { formatCurrency } from '@/utils/misc';
import { EstimateChip } from './EstimateChip';
import { KpiCard } from '../shared/KpiCard';
import { NOI_LABEL } from '../shared/metric-glossary';
import { sumMetricValues } from '../shared/metric-value';
import { MetricValue } from './MetricValue';
import { ACTUAL_ANNUALIZED_HELP, ANNUAL_ESTIMATE_HELP, formatRatio } from './metrics-copy';

interface EquityReturnCardsProps {
  metrics: InvestmentFinancialMetricsDTO;
  scaleAmount: (cents: number) => number;
}

const INITIAL_CASH_HINT =
  'Completa el coste inicial y los datos de financiación para calcular el efectivo aportado.';

const formatMetricAmount = (metric: MetricValueDTO, scaleAmount: (cents: number) => number) =>
  metric.value === null ? 'no disponible' : formatCurrency(scaleAmount(metric.value));

const ratioMetric = (numerator: number | null, denominator: MetricValueDTO): MetricValueDTO => ({
  value:
    numerator === null || denominator.value === null || denominator.value <= 0
      ? null
      : numerator / denominator.value,
  quality:
    numerator === null || denominator.value === null || denominator.value <= 0
      ? 'UNAVAILABLE'
      : 'PROVISIONAL',
});

export const EquityReturnCards = memo(({ metrics, scaleAmount }: EquityReturnCardsProps) => {
  const { equityMetrics, actualAnnualized, annualEstimate } = metrics;
  const hasAnnualEstimate = equityMetrics.operatingBasis === 'ANNUAL_ESTIMATE';
  const annualBadge = actualAnnualized ? (
    <EstimateChip label="Ritmo YTD" help={ACTUAL_ANNUALIZED_HELP} />
  ) : hasAnnualEstimate ? (
    <EstimateChip label="Previsión ficha" help={ANNUAL_ESTIMATE_HELP} />
  ) : undefined;
  const mainCashflow = actualAnnualized?.cashflowAfterDebt ?? equityMetrics.cashflowAfterDebt;
  const mainCashOnCash = actualAnnualized
    ? ratioMetric(actualAnnualized.cashflowAfterDebt.value, equityMetrics.initialCashInvested)
    : equityMetrics.cashOnCash;
  const mainCashYield = actualAnnualized
    ? ratioMetric(actualAnnualized.cashflowAfterDebt.value, equityMetrics.currentEquity)
    : equityMetrics.cashYieldOnCurrentEquity;
  const mainOperatingRoe = actualAnnualized
    ? ratioMetric(
        sumMetricValues(
          actualAnnualized.cashflowAfterDebt.value,
          actualAnnualized.principalRepaid.value
        ),
        equityMetrics.currentEquity
      )
    : equityMetrics.operatingRoe;
  const actualCashOnCash = ratioMetric(
    equityMetrics.periodCashflowAfterDebt.value,
    equityMetrics.initialCashInvested
  );
  const actualCashYield = ratioMetric(
    equityMetrics.periodCashflowAfterDebt.value,
    equityMetrics.currentEquity
  );
  const actualOperatingRoe = ratioMetric(
    sumMetricValues(
      equityMetrics.periodCashflowAfterDebt.value,
      metrics.metrics.principalRepaid.value
    ),
    equityMetrics.currentEquity
  );
  const equityUnavailableHint = !metrics.valuation
    ? 'Añade una valoración actual para calcular el equity.'
    : 'Completa los datos de financiación para calcular el equity actual.';
  // Which ficha input is missing, if the ficha estimate exists at all.
  const missingFichaInput = annualEstimate
    ? annualEstimate.cashNoi.value === null
      ? 'Completa el alquiler esperado y los gastos normalizados en la ficha.'
      : annualEstimate.debtServicePaid.value === null
        ? 'Completa los datos de financiación o la cuota mensual.'
        : undefined
    : undefined;
  const annualOperatingUnavailableHint = missingFichaInput ?? equityUnavailableHint;

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6} md={3}>
        <KpiCard
          label="Cashflow neto"
          help={`${NOI_LABEL} menos los intereses y el principal de la hipoteca. No descuenta reformas ni mejoras puntuales.`}
          badge={annualBadge}
          caption={
            actualAnnualized
              ? `Acumulado real ${formatMetricAmount(equityMetrics.periodCashflowAfterDebt, scaleAmount)}${annualEstimate ? ` · previsión ficha ${formatMetricAmount(annualEstimate.cashflowAfterDebt, scaleAmount)}` : ''}`
              : hasAnnualEstimate
                ? `Acumulado real ${formatMetricAmount(equityMetrics.periodCashflowAfterDebt, scaleAmount)}`
                : 'Después de deuda · antes de capex'
          }
        >
          <MetricValue
            metric={mainCashflow}
            format={(value) => formatCurrency(scaleAmount(value))}
            color={(mainCashflow.value ?? 0) >= 0 ? 'success.main' : 'error.main'}
          />
        </KpiCard>
      </Grid>

      <Grid item xs={12} sm={6} md={3}>
        <KpiCard
          label="Cash-on-cash"
          help="Cashflow después de deuda dividido por el efectivo que aportaste inicialmente: coste total menos principal original de la hipoteca."
          badge={annualBadge}
          caption={
            actualAnnualized
              ? `Acumulado real ${formatRatio(actualCashOnCash)}${hasAnnualEstimate ? ` · previsión ficha ${formatRatio(equityMetrics.cashOnCash)}` : ''}`
              : `Sobre ${formatMetricAmount(equityMetrics.initialCashInvested, scaleAmount)} aportados al comprar`
          }
        >
          <MetricValue
            metric={mainCashOnCash}
            format={() => formatRatio(mainCashOnCash)}
            color="primary.main"
            unavailableHint={
              !actualAnnualized && annualEstimate?.cashflowAfterDebt.value === null
                ? annualOperatingUnavailableHint
                : INITIAL_CASH_HINT
            }
          />
        </KpiCard>
      </Grid>

      <Grid item xs={12} sm={6} md={3}>
        <KpiCard
          label="Cash yield sobre equity"
          help="Cashflow después de deuda dividido por el equity actual estimado. Mide el efectivo que genera hoy el capital inmovilizado."
          badge={annualBadge}
          caption={
            actualAnnualized
              ? `Acumulado real ${formatRatio(actualCashYield)}${hasAnnualEstimate ? ` · previsión ficha ${formatRatio(equityMetrics.cashYieldOnCurrentEquity)}` : ''}`
              : `Sobre ${formatMetricAmount(equityMetrics.currentEquity, scaleAmount)} de equity actual`
          }
        >
          <MetricValue
            metric={mainCashYield}
            format={() => formatRatio(mainCashYield)}
            color="primary.main"
            unavailableHint={
              actualAnnualized ? equityUnavailableHint : annualOperatingUnavailableHint
            }
          />
        </KpiCard>
      </Grid>

      <Grid item xs={12} sm={6} md={3}>
        <KpiCard
          label="ROE operativo"
          help="Cashflow después de deuda más principal amortizado, dividido por el equity actual. Es el retorno sin contar la revalorización."
          badge={annualBadge}
          caption={
            actualAnnualized
              ? `Acumulado real ${formatRatio(actualOperatingRoe)}${hasAnnualEstimate ? ` · previsión ficha ${formatRatio(equityMetrics.operatingRoe)}` : ''}`
              : `Suma ${formatMetricAmount(annualEstimate?.principalRepaid ?? metrics.metrics.principalRepaid, scaleAmount)} de principal amortizado`
          }
        >
          <MetricValue
            metric={mainOperatingRoe}
            format={() => formatRatio(mainOperatingRoe)}
            color="success.main"
            unavailableHint={
              actualAnnualized ? equityUnavailableHint : annualOperatingUnavailableHint
            }
          />
        </KpiCard>
      </Grid>
    </Grid>
  );
});

EquityReturnCards.displayName = 'EquityReturnCards';
